import cn from "@/utils/cn"
import { toast } from "sonner"
import Toast from "./Toast"

export type CopyTextProps = {
  text: string
  label: string
  size?: "small" | "medium"
  copyButtonBehavior?: "hover" | "always" | "never"
}

const handleCopyText = async (text: string, fieldName: string) => {
  try {
    await navigator.clipboard.writeText(text)
    toast.custom((toastId) => (
      <Toast
        toastId={toastId}
        description={`${fieldName} copied to clipboard`}
      />
    ))
  } catch (error) {
    toast.error("Failed to copy text to clipboard")
  }
}

const buttonVisibilityFor = (behavior: CopyTextProps["copyButtonBehavior"]) => {
  switch (behavior) {
    case "hover":
      return "opacity-0 group-hover:opacity-100"
    case "always":
      return "opacity-100"
    case "never":
      return "opacity-0"
    default:
      return "opacity-0 group-hover:opacity-100"
  }
}

export const CopyTextButton = ({
  text,
  label,
  size = "medium",
}: CopyTextProps) => {
  return (
    <button
      type="button"
      className="absolute inset-y-0 right-0 flex items-center pr-3"
      onClick={() => handleCopyText(text, label)}
    >
      <span
        className={cn(
          "material-icons text-primary/60 hover:text-primary",
          size === "small" ? "!text-icon-sm" : "!text-icon-md"
        )}
      >
        content_copy
      </span>
    </button>
  )
}

export const CopyText = ({
  text,
  label,
  size = "medium",
  copyButtonBehavior = "hover",
  children,
}: CopyTextProps & { children?: React.ReactNode }) => {
  return (
    <div className="group flex items-center gap-2">
      {children}
      <button
        type="button"
        className={cn(
          "flex items-center gap-2",
          copyButtonBehavior === "never" && "cursor-default"
        )}
        onClick={() =>
          copyButtonBehavior !== "never" && handleCopyText(text, label)
        }
      >
        <span
          className={cn(
            "material-icons text-primary/60 hover:text-primary",
            size === "small" ? "!text-icon-sm" : "!text-icon-md",
            buttonVisibilityFor(copyButtonBehavior)
          )}
        >
          content_copy
        </span>
      </button>
    </div>
  )
}
